import React, { useState, useEffect } from 'react';
import { servicesAPI, teamAPI, blogAPI, bookingAPI, coreValuesAPI, seedAPI } from '../../../services/api';
import './Dashboard.scss';

const Dashboard = () => {
  const [stats, setStats] = useState({
    services: 0,
    team: 0,
    blog: 0,
    coreValues: 0,
    bookings: 0,
    pendingBookings: 0,
  });
  const [loading, setLoading] = useState(true);
  const [importing, setImporting] = useState(false);
  const [importResult, setImportResult] = useState(null);
  const [deleting, setDeleting] = useState(false);
  const [deleteResult, setDeleteResult] = useState(null);

  useEffect(() => {
    fetchStats();
  }, []);

  const fetchStats = async () => {
    try {
      const [services, team, blog, coreValues, bookings] = await Promise.all([
        servicesAPI.getAllAdmin(),
        teamAPI.getAllAdmin(),
        blogAPI.getAllAdmin(),
        coreValuesAPI.getAllAdmin(),
        bookingAPI.getAll({ limit: 1000 }),
      ]);
      
      const pendingBookings = bookings.data.bookings?.filter(b => b.status === 'pending').length || 0;
      
      setStats({
        services: services.data.length,
        team: team.data.length,
        blog: blog.data.length,
        coreValues: coreValues.data.length,
        bookings: bookings.data.total || 0,
        pendingBookings: pendingBookings,
      });
    } catch (error) {
      console.error('Error fetching stats:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleImportDemoData = async () => {
    if (!window.confirm('Import demo data? This will populate empty collections with sample content. Existing data will not be overwritten.')) {
      return;
    }

    setImporting(true);
    setImportResult(null);

    try {
      const response = await seedAPI.importDemoData();
      setImportResult({ success: true, data: response.data });
      fetchStats();
    } catch (error) {
      setImportResult({
        success: false,
        message: error.response?.data?.message || 'Failed to import demo data',
      });
    } finally {
      setImporting(false);
    }
  };

  const handleDeleteDemoData = async () => {
    if (!window.confirm('Are you sure you want to delete ALL demo data? This will remove all content from every collection (except admin user and bookings). This action cannot be undone.')) {
      return;
    }

    setDeleting(true);
    setDeleteResult(null);

    try {
      const response = await seedAPI.deleteDemoData();
      setDeleteResult({ success: true, data: response.data });
      fetchStats();
    } catch (error) {
      setDeleteResult({
        success: false,
        message: error.response?.data?.message || 'Failed to delete demo data',
      });
    } finally {
      setDeleting(false);
    }
  };

  if (loading) {
    return <div className="dashboard-loading">Loading...</div>;
  }

  return (
    <div className="dashboard">
      <h2>Dashboard</h2>
      <div className="stats-grid">
        <div className="stat-card">
          <div className="stat-icon">⚕️</div>
          <div className="stat-content">
            <h3>Services</h3>
            <p className="stat-number">{stats.services}</p>
          </div>
        </div>
        <div className="stat-card">
          <div className="stat-icon">👥</div>
          <div className="stat-content">
            <h3>Team Members</h3>
            <p className="stat-number">{stats.team}</p>
          </div>
        </div>
        <div className="stat-card">
          <div className="stat-icon">📝</div>
          <div className="stat-content">
            <h3>Blog Posts</h3>
            <p className="stat-number">{stats.blog}</p>
          </div>
        </div>
        <div className="stat-card">
          <div className="stat-icon">💎</div>
          <div className="stat-content">
            <h3>Core Values</h3>
            <p className="stat-number">{stats.coreValues}</p>
          </div>
        </div>
        <div className="stat-card">
          <div className="stat-icon">📅</div>
          <div className="stat-content">
            <h3>Total Bookings</h3>
            <p className="stat-number">{stats.bookings}</p>
          </div>
        </div>
        <div className="stat-card">
          <div className="stat-icon">⏳</div>
          <div className="stat-content">
            <h3>Pending Bookings</h3>
            <p className="stat-number">{stats.pendingBookings}</p>
          </div>
        </div>
      </div>
      <div className="dashboard-info">
        <h3>Welcome to Admin Panel</h3>
        <p>Use the sidebar to navigate and manage your website content.</p>
      </div>

      <div className="demo-data-section">
        <h3>Quick Setup</h3>
        <p>Populate your site with demo content. Existing data will not be overwritten.</p>
        <div className="demo-data-buttons">
          <button
            className="import-demo-btn"
            onClick={handleImportDemoData}
            disabled={importing || deleting}
          >
            {importing ? 'Importing...' : 'Import Demo Data'}
          </button>
          <button
            className="delete-demo-btn"
            onClick={handleDeleteDemoData}
            disabled={importing || deleting}
          >
            {deleting ? 'Deleting...' : 'Delete Demo Data'}
          </button>
        </div>

        {importResult && (
          <div className={`import-result ${importResult.success ? 'success' : 'error'}`}>
            <p className="import-message">
              {importResult.success ? importResult.data.message : importResult.message}
            </p>
            {importResult.success && (
              <ul className="import-details">
                {importResult.data.results.map((r, i) => (
                  <li key={i}>
                    <span className={`status-badge ${r.status === 'created' ? 'created' : 'skipped'}`}>
                      {r.status === 'created' ? 'Created' : 'Skipped'}
                    </span>
                    {r.collection}{r.count ? ` (${r.count})` : ''}
                  </li>
                ))}
              </ul>
            )}
          </div>
        )}

        {deleteResult && (
          <div className={`import-result ${deleteResult.success ? 'success' : 'error'}`}>
            <p className="import-message">
              {deleteResult.success ? deleteResult.data.message : deleteResult.message}
            </p>
            {deleteResult.success && (
              <ul className="import-details">
                {deleteResult.data.results.map((r, i) => (
                  <li key={i}>
                    <span className={`status-badge ${r.deleted > 0 ? 'deleted' : 'skipped'}`}>
                      {r.deleted > 0 ? `Deleted ${r.deleted}` : 'Empty'}
                    </span>
                    {r.collection}
                  </li>
                ))}
              </ul>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

export default Dashboard;
